import React from "react";

export interface ConditionProps {
  condition?: (() => boolean) | boolean;
  children?: React.ReactNode;
}

export interface ConditionalProps {
  children?: React.ReactNode;
}

const If: React.FC<ConditionProps> = (props) => props.children as React.ReactElement;

const Else: React.FC<ConditionProps> = (props) => {
  if (props.condition !== undefined) console.warn("Else cannot contain a condition, The condition is ignored.");
  return props.children as React.ReactElement;
};

const ElseIf: React.FC<ConditionProps> = (props) => {
  const conditionMet = evaluateConditionProp(props.condition);
  return conditionMet ? (props.children as React.ReactElement) : null;
};

const isFunction = (possibleFunction: any): boolean => {
  return typeof possibleFunction === "function";
};

const evaluateConditionProp = (condition?: (() => boolean) | boolean): boolean => {
  return isFunction(condition) ? (condition as () => boolean)() : !!condition;
};

// If multiple children
// - All children should be one of 'If', 'Else', 'ElseIf'.
// - First child should be 'If'.
// - Else should only be used once.
// - Else should be used at last.
// If only one child
// - It must be 'If'
const hasValidChildren = (children: React.ReactNode): boolean => {
  if (!children) {
    console.warn("No Children found, Conditional must contain at least one 'If' Component as child component.");
    return false;
  }
  let childrenArray = React.Children.toArray(children);
  if (
    !childrenArray.every(
      (child) =>
        (child as React.ReactElement).type === If ||
        (child as React.ReactElement).type === Else ||
        (child as React.ReactElement).type === ElseIf,
    )
  ) {
    throw new Error("Invalid children found, Child for 'Conditional' should be one of 'If', 'Else', 'ElseIf'.");
  }
  if ((childrenArray[0] as React.ReactElement).type !== If) {
    throw new Error("First Children must be an 'If' component");
  }
  let elseChildren = childrenArray.filter((child) => (child as React.ReactElement).type === Else);
  let ifChildren = childrenArray.filter((child) => (child as React.ReactElement).type === If);

  if (ifChildren.length > 1) throw new Error("'If' should be used only once.");

  if (elseChildren.length > 1) {
    throw new Error("Else should be used only once.");
  } else if (elseChildren.length === 0) {
    return true;
  } else {
    if ((childrenArray[childrenArray.length - 1] as React.ReactElement).type !== Else) {
      throw new Error("Else should be used at last.");
    } else {
      return true;
    }
  }
};

const evaluateChildren = (children: React.ReactNode): React.ReactNode => {
  let childrenArray = React.Children.toArray(children);
  let i = 0;
  for (; i < childrenArray.length; i++) {
    let child = childrenArray[i];
    if ((child as React.ReactElement).type === If || (child as React.ReactElement).type === ElseIf) {
      if (evaluateConditionProp((child as React.ReactElement).props.condition)) {
        return child;
      }
    } else {
      break;
    }
  }
  for (; i < childrenArray.length; i++) {
    let child = childrenArray[i];
    if ((child as React.ReactElement).type === Else) {
      return child;
    }
  }
  return null;
};

const Conditional: React.FC<ConditionalProps> = ({ children }) => {
  if (!hasValidChildren(children)) return null;
  return evaluateChildren(children) as React.ReactElement;
};

export { Conditional, If, Else, ElseIf };
